import { type ReactNode } from "react";

import { BTDropdownButton } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { Statuses } from "@btnet/services-btnet/handlers/models/statuses";

import { BdsIconPaperPlaneRight } from "@buildertrend/components";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import {
    type IBTItemHeaderMenuAction,
    type IBTItemHeaderSecondaryAction,
} from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import { shouldShowApproveAndDeclineActionsColumn } from "commonComponents/utilities/SelectionOption/SelectionOption.utils";

export interface ISelectionOptionHeaderActionsProps {
    disabled: boolean;
    isParsingProductUrl: boolean;
    hasDeletePermission: boolean;
    hasUpdatePermission: boolean;
    isCreating: boolean;
    isEditing: boolean;
    isExistingEntity: boolean;
    isModal?: boolean;
    status?: number;
    onCancel: () => void;
    onDelete: () => void;
    onEdit: () => void;
    onSave: () => void;
    onApprove?: () => void;
    onDecline?: () => void;
    onReset?: () => void;
    onSend?: () => void;
    onCopy: () => void;
    canCopy: boolean;
    canSaveToCatalog: boolean;
    onSaveToCatalog: () => void;
    isSavingToCatalog: boolean;
    canCreatePurchaseOrder?: boolean;
    canAddToExistingPurchaseOrder?: boolean;
    openPurchaseOrderLinkingWizard: (toExisting: boolean) => void;
    /** When set, Send/Approve/Decline are disabled and show this message as a hover tooltip. */
    coBlockMessage?: string | null;
}

/** The action groupings `SelectionOptionHeader` passes through to `DetailFrame.ActionButtons`. */
export interface ISelectionOptionHeaderActionsResult {
    /** Send — the header's sole primary action. */
    primaryAction: ReactNode;
    /**
     * Inline workflow actions (Save, Cancel, Recall, Add to PO, and the client's primary
     * Approve). Save is first so it renders to the left of Send (secondary actions render
     * before the primary action in `DetailFrame.ActionButtons`' DOM order). Save and Cancel
     * are pinned so they stay visible instead of collapsing into the overflow menu.
     */
    secondaryActions: (ReactNode | IBTItemHeaderSecondaryAction)[];
    /**
     * Approve, Decline, Copy, Delete, and Save to catalog — menu-only actions that always
     * live in the single `BTItemHeader` overflow ("More") menu, per the Figma design. Delete
     * renders in the destructive style.
     */
    menuActions: IBTItemHeaderMenuAction[];
}

export function useSelectionOptionHeaderActions(
    props: Readonly<ISelectionOptionHeaderActionsProps>,
): ISelectionOptionHeaderActionsResult {
    const {
        disabled,
        isParsingProductUrl,
        hasDeletePermission,
        hasUpdatePermission,
        isCreating,
        isEditing,
        isExistingEntity,
        isModal = false,
        status,
        canCreatePurchaseOrder = false,
        canAddToExistingPurchaseOrder = false,
        openPurchaseOrderLinkingWizard,
        coBlockMessage,
    } = props;
    const userInfo = useRequiredContext(UserInfoContext);
    const isBuilder = userInfo.loginType === BTLoginTypes.BUILDER;
    const isClient =
        userInfo.loginType === BTLoginTypes.OWNER || userInfo.loginType === BTLoginTypes.CONTACT;

    const shouldShowApproveDeclineActions =
        isExistingEntity &&
        shouldShowApproveAndDeclineActionsColumn(userInfo.loginType, hasUpdatePermission);

    // The reset button will not be present on the client portal.
    // Recall applies to Sent (PENDING) options too — transitions them back to Draft.
    const showReset =
        isBuilder &&
        shouldShowApproveDeclineActions &&
        (status === Statuses.APPROVED ||
            status === Statuses.DECLINED ||
            status === Statuses.PENDING);

    // Send is the primary action for builders on draft options — transitions Draft → Sent.
    const showSend = isBuilder && shouldShowApproveDeclineActions && status === Statuses.UNRELEASED;

    const isEditableStatus = status === Statuses.PENDING || status === Statuses.UNRELEASED;

    const showDecline = shouldShowApproveDeclineActions && isEditableStatus;
    const showApprove = shouldShowApproveDeclineActions && isEditableStatus;

    // The delete button will not be present on the client portal
    const showDelete =
        isBuilder &&
        hasDeletePermission &&
        isExistingEntity &&
        (status === Statuses.PENDING ||
            status === Statuses.DECLINED ||
            status === Statuses.UNRELEASED);

    const showCancel = isCreating || (isModal && !isEditing && isBuilder);
    const showSave =
        isCreating || ((isEditableStatus || status == null) && hasUpdatePermission && isEditing);

    const hasPrimaryApproveButton = isClient && status === Statuses.PENDING;

    // If the user is a builder and is viewing the selection option in a modal,
    // we will hide the secondary header buttons to prevent editing.
    const hideSecondaryHeaderButtons = isBuilder && isModal;

    const showAddToPurchaseOrder = canCreatePurchaseOrder || canAddToExistingPurchaseOrder;

    const showSecondaryHeaderButtons = !hideSecondaryHeaderButtons;

    // Send, when shown, is the primary action for a Draft option. It transitions Draft → Sent.
    const sendButton = showSend && (
        <BTPopover
            content={coBlockMessage ? "A change order needs to be approved first" : undefined}
        >
            <span>
                <BTButton
                    type="primary"
                    data-testid="send"
                    icon={<BdsIconPaperPlaneRight />}
                    disabled={disabled || !!coBlockMessage}
                    onClick={props.onSend}
                >
                    Send
                </BTButton>
            </span>
        </BTPopover>
    );

    // Save is styled primary when it's the only high-emphasis action, and demoted to secondary
    // when Send is present (one primary action per screen). Placement follows suit: Save takes the
    // primary (rightmost) slot when there's no Send, and renders as a secondary to Send's left when
    // Send is present — so the primary action is always rightmost. Single button (no split menu).
    const saveButton = showSave && (
        <BTButton
            data-testid="save"
            type={showSend ? "secondary" : "primary"}
            disabled={disabled || isParsingProductUrl}
            onClick={props.onSave}
        >
            Save
        </BTButton>
    );

    // Approve/Decline are the client's main workflow actions, so a client viewing a PENDING option
    // gets BOTH inline (Decline styled secondary, Approve styled primary) rather than in the overflow
    // menu — the recommended path is for the client to make this decision. Both are pinned in
    // secondaryActions below so they stay inline on mobile. This is distinct from the builder, whose
    // Approve/Decline live in the menu. Both keep the coBlockMessage disable + tooltip.
    const clientDeclineAction = showSecondaryHeaderButtons &&
        showDecline &&
        hasPrimaryApproveButton && (
            <BTPopover
                content={coBlockMessage ? "A change order needs to be approved first" : undefined}
            >
                <span>
                    <BTButton
                        type="secondary"
                        data-testid="decline"
                        aria-label="Decline"
                        disabled={disabled || !!coBlockMessage}
                        onClick={props.onDecline}
                    >
                        Decline
                    </BTButton>
                </span>
            </BTPopover>
        );

    const clientApproveAction = showSecondaryHeaderButtons &&
        showApprove &&
        hasPrimaryApproveButton && (
            <BTPopover
                content={coBlockMessage ? "A change order needs to be approved first" : undefined}
            >
                <span>
                    <BTButton
                        type="primary"
                        data-testid="approve"
                        aria-label="Approve"
                        disabled={disabled || !!coBlockMessage}
                        onClick={props.onApprove}
                    >
                        Approve
                    </BTButton>
                </span>
            </BTPopover>
        );

    // The primary (rightmost) slot: Send on a Draft, otherwise Save. When Save is primary it is
    // NOT also listed in secondaryActions below. The client's primary-styled Approve is a pinned
    // secondary (see below) rather than this slot, so it stays inline alongside its Decline.
    const primaryAction = sendButton || saveButton || null;
    // Save is a secondary (left of Send) only when Send holds the primary slot.
    const saveAction = showSend ? saveButton : false;

    const cancelAction = showSecondaryHeaderButtons && showCancel && (
        <BTButton
            type="secondary"
            data-testid="cancel"
            disabled={disabled || isParsingProductUrl}
            onClick={props.onCancel}
        >
            Cancel
        </BTButton>
    );

    const resetAction = showSecondaryHeaderButtons && showReset && (
        <BTButton type="secondary" data-testid="reset" disabled={disabled} onClick={props.onReset}>
            Recall
        </BTButton>
    );

    const addToPurchaseOrderAction = showSecondaryHeaderButtons && showAddToPurchaseOrder && (
        <BTDropdownButton
            data-testid="addToDropdown"
            type="secondary"
            menuItems={[
                {
                    "data-testid": "newPurchaseOrder",
                    content: "New PO",
                    disabled: !canCreatePurchaseOrder,
                    onClick: () => openPurchaseOrderLinkingWizard(false),
                },
                {
                    "data-testid": "existingPurchaseOrder",
                    content: "Existing PO",
                    disabled: !canAddToExistingPurchaseOrder,
                    onClick: () => openPurchaseOrderLinkingWizard(true),
                },
            ]}
        >
            Add to
        </BTDropdownButton>
    );

    // Approve, Decline, Copy, Save to catalog, and Delete are menu-only actions that always live
    // in the single BTItemHeader overflow ("More") menu. Ordering follows the standard menu
    // hierarchy: workflow decisions (Approve/Decline) first, then item operations (Copy, Save to
    // catalog), then the destructive Delete last — separated by a divider and styled destructively.
    // The builder's Approve/Decline intentionally live here rather than as prominent buttons: the
    // recommended path is for the client to approve/decline, so Send/Save stay the primary CTAs.
    // Approve/Decline are hidden while change-order-blocked (they can't be actioned until the
    // change order is approved). The client's primary Approve is the inline CTA above.
    const menuActions: IBTItemHeaderMenuAction[] = [
        {
            label: "Approve",
            testid: "approve",
            onClick: () => props.onApprove?.(),
            disabled,
            isHidden: !(
                showSecondaryHeaderButtons &&
                showApprove &&
                !hasPrimaryApproveButton &&
                !coBlockMessage
            ),
        },
        {
            label: "Decline",
            testid: "decline",
            onClick: () => props.onDecline?.(),
            disabled,
            isHidden: !(
                showSecondaryHeaderButtons &&
                showDecline &&
                !hasPrimaryApproveButton &&
                !coBlockMessage
            ),
        },
        {
            label: "Copy",
            testid: "copy",
            onClick: () => props.onCopy(),
            disabled,
            isHidden: !(showSecondaryHeaderButtons && props.canCopy),
        },
        {
            label: "Save to catalog",
            testid: "saveToCatalog",
            onClick: () => props.onSaveToCatalog(),
            disabled: disabled || props.isSavingToCatalog,
            isHidden: !(showSecondaryHeaderButtons && props.canSaveToCatalog),
        },
        {
            label: "Delete",
            testid: "delete",
            onClick: () => props.onDelete(),
            disabled,
            isHidden: !(showSecondaryHeaderButtons && showDelete),
            danger: true,
            dividerBefore: true,
        },
    ];

    return {
        primaryAction,
        secondaryActions: [
            // Save, Cancel, and Recall are pinned so they stay visible on narrow viewports instead
            // of collapsing into the overflow menu. Recall is the builder's main action on a Sent
            // option (Send is gone by then), so it should stay surfaced alongside Save.
            saveAction && { node: saveAction, pinned: true },
            cancelAction && { node: cancelAction, pinned: true },
            resetAction && { node: resetAction, pinned: true },
            addToPurchaseOrderAction,
            // A client's Approve/Decline are their main workflow actions, so both are pinned to stay
            // inline on narrow viewports instead of collapsing into the menu. Decline (secondary)
            // renders to the left of the primary-styled Approve, which sits rightmost.
            clientDeclineAction && { node: clientDeclineAction, pinned: true },
            clientApproveAction && { node: clientApproveAction, pinned: true },
        ].filter(Boolean),
        menuActions,
    };
}
